import React, { useState, useEffect } from "react";
import Header, {
  HeaderNav,
  Menu,
  MobileMenu,
} from "../../Components/Header/Header";
import { Col, Navbar } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Buttons from "../../Components/Button/Buttons";
import Footer from "../../Components/Footer";
import ModalPopupContact from "../../Components/ModalPopupContact";
import SearchButton from "../../Components/SearchButton";
import Fuse from "fuse.js";
import { Helmet } from "react-helmet";
import { generateHeaderData } from "../../Components/Header/HeaderDataGenerator";
import { generateHeaderDataMobile } from "../../Components/Header/HeaderDataMobileGenerator";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../hooks/useLanguage";

const BlogLayout = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, ready } = useTranslation(['common'], { useSuspense: false });
  const { getLocalizedPath } = useLanguage();
  const localizedBlogPath = getLocalizedPath('/blog');

  // Generate menu data based on current language (only when translations are ready)
  const headerData = ready ? generateHeaderData(t, getLocalizedPath) : [];
  const headerDataMobile = ready ? generateHeaderDataMobile(t, getLocalizedPath) : [];

  // Debug: log the generated menu data
  useEffect(() => {
    console.log('Blog Layout - Translation ready:', ready);
    console.log('Blog Layout - headerData:', headerData);
    console.log('Blog Layout - headerDataMobile:', headerDataMobile);
  }, [ready, headerData, headerDataMobile]);

  // Listen for body class changes to detect when mobile menu opens/closes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          const bodyHasMenuOpenClass = document.body.classList.contains(
            "navbar-collapse-show"
          );
          setIsMobileMenuOpen(bodyHasMenuOpenClass);
        }
      });
    });

    observer.observe(document.body, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleSearch = (query) => {
    // Initialize Fuse instance with your search data
    const searchData = [
      // Add your searchable content here
      // Example: { title: 'Blog Post 1', content: '...' }
    ];

    const fuseOptions = {
      keys: ["title", "content"], // specify which fields to search
      threshold: 0.3, // A lower threshold means a more strict match
      distance: 100,
    };

    const fuse = new Fuse(searchData, fuseOptions);
    const results = fuse.search(query);

    // Handle search results
    console.log(results);
    // Implement your search results display logic here
  };

  return (
    <div className='blogpage'>
      <Helmet>
        <title>INAQ Blog - Expertné long-form články o marketingu</title>
        <meta
          name='description'
          content='Sme experti na marketing a o našich skúsenostiach a postrehoch píšeme na blogu. Kliknite a prečítajte si naše odporúčania a rady z oblasti marketingu.'
        />
        <meta
          name='keywords'
          content='B2B marketing, marketingové tipy, B2B stratégie, blog o marketingu, B2B marketing blog,  reklamná agentúra, INAQ'
        />
      </Helmet>
      {/* Header Start */}
      <Header topSpace={{ md: true }} type='reverse-scroll'>
        <HeaderNav
          theme='light'
          expand='lg'
          bg='purple'
          containerClass='sm:!px-0 max-w-[1280px]'
          className='py-[0px] px-[35px] lg:px-[15px] md:pr-[15px] md:pl-[10px] md:py-[20px] mx-auto bg-inaqpurple'
          fluid='fluid'
        >
          <Col sm={6} lg={2} xs='auto' className='me-auto ps-0'>
            <Link
              aria-label='logo home'
              className='flex items-center'
              to={localizedBlogPath}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <Navbar.Brand className='inline-block p-0 m-0'>
                <img
                  className='default-logo'
                  width='56'
                  height='36'
                  loading='lazy'
                  src='/assets/img/webp/inaq_blog_logo_web.webp'
                  data-rjs='/assets/img/webp/inaq_blog_logo_web.webp'
                  alt='logo'
                />

                <img
                  className='mobile-logo'
                  width='111'
                  height='36'
                  loading='lazy'
                  src='/assets/img/webp/inaq_blog_logo_web.webp'
                  data-rjs='/assets/img/webp/inaq_logo_web.webp'
                  alt='logo'
                />
              </Navbar.Brand>
            </Link>
          </Col>

          {/* Desktop Menu - Only visible on large screens */}
          <div className='col-auto justify-center menu-order inaq-header-menu px-lg-0 col-lg-8 d-none d-lg-block'>
            <Menu data={headerData} openModal={() => setIsModalOpen(true)} />
          </div>

          {/* Search and Contact Buttons - Hidden when mobile menu is open */}
          <Col
            xs='auto'
            lg={2}
            className={`text-right pe-0 ${isMobileMenuOpen ? "md:hidden" : ""}`}
          >
            <div className='flex items-center justify-end gap-2 lg:gap-3'>
              <div
                className={`md:block lg:block ${isMobileMenuOpen ? "hidden" : ""}`}
              >
                <SearchButton onSearch={handleSearch} />
              </div>

              <Buttons
                type='button'
                onClick={() => setIsModalOpen(true)}
                className={`!rounded-sm ml-2 font-medium font-rubin uppercase shadow-[0_0_30px_rgba(0,0,0,0.08)] md:hidden ${isMobileMenuOpen ? "hidden" : ""}`}
                themeColor={["#fff", "#fff"]}
                size='xs'
                color='#212529'
                title={t('buttons.nonCommittalInquiry', 'Nezáväzný dopyt')}
              />
            </div>
          </Col>

          {/* Mobile Menu */}
          {console.log('Rendering MobileMenu with data:', headerDataMobile)}
          <MobileMenu data={headerDataMobile} openModal={() => setIsModalOpen(true)} />
        </HeaderNav>
      </Header>

      {/* Contact Modal */}
      <ModalPopupContact
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);

          // Force close the mobile menu when modal is closed
          document.body.classList.remove("navbar-collapse-show");
          const navbarCollapse = document.querySelector(".navbar-collapse");
          const toggleButton = document.querySelector(".navbar-toggler");

          if (navbarCollapse) {
            navbarCollapse.classList.remove("show");
          }

          if (toggleButton) {
            toggleButton.classList.add("collapsed");
            toggleButton.setAttribute("aria-expanded", "false");
          }

          // Reset the isMobileMenuOpen state
          setIsMobileMenuOpen(false);
        }}
      />

      {/* Main Content */}
      <main className='blog-content'>{children}</main>

      {/* Footer */}
      <Footer
        className='inaq-footer'
        theme='light'
        logo='/assets/img/webp/inaq_logo.svg'
        width='56'
        loading='lazy'
      >
        {/* Cookie settings button */}
        <div
          className='cookie-settings-container'
          style={{ textAlign: "center", marginTop: "20px" }}
        >
          <button
            type='button'
            data-cc='show-preferencesModal'
            className='nav-link'
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            {t('footer.privacySettings', 'Nastavenie súkromia')}
          </button>
        </div>
      </Footer>
    </div>
  );
};

export default BlogLayout;
